import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { ordersAPI, companiesAPI, aalignAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ShoppingBag,
  Package,
  Clock,
  CheckCircle,
  ArrowRight,
  Building2,
  TrendingUp,
  AlertCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, companyRes] = await Promise.all([
        ordersAPI.getBuyerOrders(),
        companiesAPI.getMy().catch(() => ({ data: null }))
      ]);
      setOrders(ordersRes.data);
      setCompany(companyRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-sky-100 text-sky-800',
      in_fulfillment: 'bg-blue-100 text-blue-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const stats = {
    total_orders: orders.length,
    active_orders: orders.filter(o => ['pending', 'approved', 'in_fulfillment'].includes(o.status)).length,
    total_leads: orders.reduce((acc, o) => acc + o.leads_delivered, 0),
    total_spent: orders.filter(o => o.status !== 'cancelled').reduce((acc, o) => acc + o.total_price, 0),
  };

  if (loading) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="buyer-dashboard">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">
              Welcome back, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-slate-600">Manage your lead campaigns and orders</p>
          </div>
          <Button onClick={() => navigate('/marketplace')} data-testid="browse-marketplace-btn">
            <Plus className="w-4 h-4 mr-2" />
            Browse Marketplace
          </Button>
        </div>

        {/* Company Setup Banner */}
        {!company && (
          <Card className="mb-6 border-sky-200 bg-sky-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <div className="font-medium text-sky-900">Complete your company profile</div>
                    <div className="text-sm text-sky-700">Add your company details for better lead matching</div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/profile')}>
                  Setup Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.total_orders}</div>
                <div className="stat-card-label">Total Orders</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.active_orders}</div>
                <div className="stat-card-label">Active Orders</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="stat-card-value">{stats.total_leads}</div>
                <div className="stat-card-label">Leads Received</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="stat-card-value">${stats.total_spent.toLocaleString()}</div>
                <div className="stat-card-label">Total Invested</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Orders</CardTitle>
            <Link to="/buyer/orders" className="text-sm text-sky-600 hover:text-sky-700">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">No orders yet</h3>
                <p className="text-slate-500 mb-4">Start by browsing our marketplace</p>
                <Button onClick={() => navigate('/marketplace')}>
                  Browse Marketplace
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.slice(0, 5).map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/buyer/orders/${order.id}`)}
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium text-slate-900">{order.campaign_name}</span>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-500">
                        {order.listing_title} • {order.quantity} leads
                      </div>
                      {order.leads_delivered > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>Delivery Progress</span>
                            <span>{order.leads_delivered} / {order.quantity}</span>
                          </div>
                          <Progress 
                            value={(order.leads_delivered / order.quantity) * 100} 
                            className="h-1.5"
                          />
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-semibold text-slate-900">${order.total_price.toLocaleString()}</div>
                      <div className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 ml-4" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BuyerDashboard;
